import { EditPencil } from '@/components';
import { DeleteColumn } from '@/components/DeleteColumn/DeleteColumn';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { RoutePath } from '@/core/router';
import { AutomationModel } from '@/modules/Automations/domain';
import { secondsToTimeDurationString } from '@/utils';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type AssociatedAutomationColumnsProps = {
  onEditClick?: (row: AutomationModel) => void;
  onDeleteClick?: (row: AutomationModel) => void;
  canEdit?: (row: AutomationModel) => boolean;
  disableDeleteMessage?: string;
};

export const useAssociatedAutomationsColumns = ({
  canEdit = () => true,
  onEditClick,
  disableDeleteMessage,
  onDeleteClick,
}: AssociatedAutomationColumnsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return useMemo<SortableColumnDef<AutomationModel>[]>(
    () => [
      {
        id: 'name',
        name: t('automation_name'),
        accessor: 'name',
        alignment: 'left',
        tdClassNames: 'max-w-[15vh]',
        renderRow: (row) => (
          <div
            className="cursor-pointer overflow-hidden text-ellipsis font-black"
            onClick={() =>
              navigate(RoutePath.automationDetail(row.id.toString()))
            }
          >
            {row.name}
          </div>
        ),
      },
      {
        id: 'code',
        name: t('code'),
        accessor: 'code',
      },
      {
        id: 'businessAreaName',
        name: t('code'),
        accessor: 'businessAreaName',
        renderRow: (row) => (
          <div className="overflow-hidden text-ellipsis">
            {row.businessAreaName}
          </div>
        ),
      },
      {
        id: 'stage',
        name: t('stage'),
        accessor: 'stage',
        renderRow: (row) => (
          <div className="overflow-hidden text-ellipsis">
            {row.businessAreaName}
          </div>
        ),
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => (
          <Trans i18nKey={row.status ? 'active' : 'inactive'} />
        ),
      },
      {
        id: 'id',
        name: t('id'),
        accessor: 'id',
        renderRow: (row) => (
          <div className="overflow-hidden text-ellipsis">{row.id}</div>
        ),
      },
      {
        id: 'divider-1',
        name: t('divider'),
        renderRow: () => <div className="overflow-hidden text-ellipsis">|</div>,
        sortable: false,
      },
      {
        id: 'runTime',
        name: t('run_time'),
        accessor: 'runTime',
        renderRow: (row) => (
          <div className="overflow-hidden text-ellipsis">
            {secondsToTimeDurationString(+row.runTime)}
          </div>
        ),
        sortable: false,
      },
      {
        id: 'averageCases',
        name: t('average_cases'),
        accessor: 'averageCases',
        renderRow: (row) => (
          <div className="overflow-hidden text-ellipsis">
            {Math.trunc(row.averageCases)}
          </div>
        ),
        sortable: false,
      },
      {
        id: 'success',
        name: t('success_percentage'),
        accessor: 'success',
        renderRow: (row) => (
          <div className="overflow-hidden text-ellipsis">
            {row.success.toFixed(2)}%
          </div>
        ),
        sortable: false,
      },
      {
        id: 'divider-2',
        name: t('divider'),
        renderRow: () => <div className="overflow-hidden text-ellipsis">|</div>,
        sortable: false,
      },
      {
        id: 'actions',
        name: t('actions'),
        renderRow: (row) => (
          <div className="flex items-center space-x-4">
            {canEdit(row) && (
              <EditPencil
                className="cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditClick?.(row);
                }}
              />
            )}
            <DeleteColumn
              disabled={!row.canDelete}
              disabledMessage={disableDeleteMessage}
              onClick={() => onDeleteClick?.(row)}
              className="pt-1"
            />
          </div>
        ),
        sortable: false,
      },
    ],
    [canEdit, disableDeleteMessage, navigate, onDeleteClick, onEditClick, t],
  );
};
